import type { CronTimeFrame } from '@/types/schedule';
import type { FrequencyUnit } from '@/utils/cron/types';

import { applyCronTimeFrameToForm, hasStoredCronTimeFrame } from '../utils/cronTimeFrame';
import { formatCronString, prettyExpression } from '../utils/cronUtils';
import {
  type RecurringCronFormState,
  describeLocalRecurringSchedule,
  describeUtcRecurringSchedule,
  parseRecurringCronFormState,
  recurringFormToScheduleConfig,
} from '../utils/recurringCronSchedule';
import {
  formatUtcTimeFrameRangeFromAuthorWindow,
  formatViewerLocalTimeFrameRangeFromAuthorWindow,
} from '../utils/recurringCronValidation';
import {
  convertLocalTimeToUTC,
  convertUTCToLocalTime,
  convertUtcCronInstantToLocalWallClockSchedule,
  getBrowserTimezone,
} from '../utils/timezoneData';

export type ScheduleTimeDisplayMode = 'local' | 'utc';

export type AdvancedCronSubType = 'recurring' | 'fixed-time' | 'recurring-fixed-time';

export interface AdvancedCronEditState {
  mode: AdvancedCronSubType;
  frequencyUnit?: FrequencyUnit;
  frequency?: number;
  startingMinute?: number;
  useTimeFrame?: boolean;
  hoursFrom?: number;
  hoursTo?: number;
  dayOfWeek?: string;
  dayOfMonth?: string;
  month?: string;
  fixedHour?: number;
  fixedMinute?: number;
  fixedYear?: string;
  fixedMonth?: number;
  fixedDay?: number;
}

const SINGLE_HOUR_PATTERN = /^\d+$/;
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

function appendUtcSuffix(label: string): string {
  const base = label.replace(/\s*\(UTC\)\s*$/i, '').trim();
  if (!base) {
    return '';
  }
  return /\bUTC$/i.test(base) ? base : `${base} UTC`;
}

function formatUtcWallClock(hour: number, minute: number): string {
  const h12 = hour % 12 || 12;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${h12}:${String(minute).padStart(2, '0')} ${ampm}`;
}

function describeCronExpressionUtc(cronExpression: string): string {
  if (!cronExpression.trim()) {
    return '';
  }
  return formatCronString(prettyExpression(cronExpression));
}

/** Classify stored EventBridge cron into advanced sub-type. */
export function classifyAdvancedCron(expression: string): AdvancedCronSubType | null {
  const parts = expression.trim().split(/\s+/);
  if (parts.length < 5) {
    return null;
  }

  const [minuteField, hourField, , , , yearField] = parts;
  if (yearField && yearField !== '*' && /^\d{4}$/.test(yearField)) {
    return 'fixed-time';
  }

  const hasMinuteStep = minuteField.includes('/');
  const hasHourStep = hourField.includes('/');
  const hasHourRange = hourField.includes('-');
  const isCommaHourList =
    hourField.includes(',') &&
    !hourField.includes('/') &&
    hourField.split(',').every((part) => SINGLE_HOUR_PATTERN.test(part.trim()));

  if (hasMinuteStep || hasHourStep || hasHourRange || isCommaHourList) {
    return 'recurring';
  }

  if (SINGLE_HOUR_PATTERN.test(hourField) && SINGLE_HOUR_PATTERN.test(minuteField)) {
    return 'recurring-fixed-time';
  }

  return null;
}

function recurringFormToEditState(form: RecurringCronFormState): AdvancedCronEditState {
  return {
    mode: 'recurring',
    frequencyUnit: form.frequencyUnit,
    frequency: form.frequency,
    startingMinute: form.startingMinute,
    useTimeFrame: form.useTimeFrame,
    hoursFrom: form.hoursFrom,
    hoursTo: form.hoursTo,
    dayOfWeek: form.dayOfWeek,
    dayOfMonth: form.dayOfMonth,
    month: form.month,
  };
}

/** UTC-only human-readable frequency for recurring advanced cron. */
export function formatAdvancedRecurringFrequencyUtc(cronExpression: string): string | null {
  const recurring = describeUtcRecurringSchedule(cronExpression);
  if (!recurring) {
    return null;
  }
  return appendUtcSuffix(recurring);
}

/** UTC-only human-readable frequency for one-shot fixed-time cron. */
export function formatAdvancedFixedTimeFrequencyUtc(
  cronExpression: string,
  cronExplain: string | undefined
): string {
  if (cronExplain?.trim()) {
    return appendUtcSuffix(cronExplain);
  }

  if (!cronExpression.trim()) {
    return '';
  }

  const parts = cronExpression.trim().split(/\s+/);
  const [minuteField, hourField, domField, monthField, , yearField] = parts;
  const minute = parseInt(minuteField, 10);
  const hour = parseInt(hourField, 10);
  const day = parseInt(domField, 10);
  const month = parseInt(monthField, 10);
  const year = yearField;

  if (
    !Number.isNaN(minute) &&
    !Number.isNaN(hour) &&
    !Number.isNaN(day) &&
    !Number.isNaN(month) &&
    year &&
    /^\d{4}$/.test(year)
  ) {
    const monthName = MONTH_NAMES[month - 1] ?? String(month);
    return `At ${formatUtcWallClock(hour, minute)} on ${monthName} ${day}, ${year} UTC`;
  }

  return appendUtcSuffix(describeCronExpressionUtc(cronExpression));
}

/** UTC-only human-readable frequency for recurring fixed-time cron. */
export function formatAdvancedRecurringFixedFrequencyUtc(
  cronExpression: string,
  cronExplain: string | undefined
): string {
  if (cronExplain?.trim()) {
    return appendUtcSuffix(cronExplain);
  }

  if (!cronExpression.trim()) {
    return '';
  }

  const parts = cronExpression.trim().split(/\s+/);
  const [minuteField, hourField] = parts;
  const minute = parseInt(minuteField, 10);
  const hour = parseInt(hourField, 10);

  if (!Number.isNaN(minute) && !Number.isNaN(hour)) {
    return `At ${formatUtcWallClock(hour, minute)} UTC`;
  }

  return appendUtcSuffix(describeCronExpressionUtc(cronExpression));
}

/**
 * Advanced cron frequency with Local/UTC toggle (aligned with Daily/Repeating).
 */
function formatAdvancedCronFrequencyFromAuthorWindowInUtc(
  cronExpression: string,
  cronTimeZone: string,
  cronTimeFrame?: CronTimeFrame | null
): string | null {
  if (classifyAdvancedCron(cronExpression) !== 'recurring') {
    return null;
  }

  const parsed = parseRecurringCronFormState(cronExpression, cronTimeZone);
  if (!parsed) {
    return null;
  }

  const form = hasStoredCronTimeFrame(cronTimeFrame)
    ? applyCronTimeFrameToForm(parsed, cronTimeFrame)
    : parsed;

  if (!form.useTimeFrame) {
    return null;
  }

  const rangeText = formatUtcTimeFrameRangeFromAuthorWindow(
    form.hoursFrom,
    form.startingMinute,
    form.hoursTo,
    cronTimeZone,
    new Date(),
    form.frequencyUnit
  );

  if (form.frequencyUnit === 'minutes') {
    const desc = `Every ${form.frequency} minute${form.frequency === 1 ? '' : 's'}, ${rangeText}`;
    return appendUtcSuffix(desc);
  }

  const startUtc = convertLocalTimeToUTC(form.hoursFrom, form.startingMinute, cronTimeZone);
  const desc = `At ${String(startUtc.minute).padStart(2, '0')} minutes past the hour, every ${form.frequency} hour${form.frequency === 1 ? '' : 's'}, ${rangeText}`;
  return appendUtcSuffix(desc);
}

function formatAdvancedCronFrequencyInViewerLocal(
  form: RecurringCronFormState,
  authorTimeZone: string,
  viewerTimeZone: string
): string {
  if (form.frequencyUnit === 'minutes') {
    let desc = `Every ${form.frequency} minute${form.frequency === 1 ? '' : 's'}`;
    if (form.useTimeFrame) {
      desc += `, ${formatViewerLocalTimeFrameRangeFromAuthorWindow(
        form.hoursFrom,
        form.startingMinute,
        form.hoursTo,
        authorTimeZone,
        viewerTimeZone,
        new Date(),
        form.frequencyUnit
      )}`;
    }
    return desc;
  }

  const startMin = form.startingMinute;
  let desc = `At ${String(startMin).padStart(2, '0')} minutes past the hour, every ${form.frequency} hour${form.frequency === 1 ? '' : 's'}`;
  if (form.useTimeFrame) {
    desc += `, ${formatViewerLocalTimeFrameRangeFromAuthorWindow(
      form.hoursFrom,
      form.startingMinute,
      form.hoursTo,
      authorTimeZone,
      viewerTimeZone,
      new Date(),
      form.frequencyUnit
    )}`;
  }
  return desc;
}

export function formatAdvancedCronFrequency(
  cronExpression: string,
  cronExplain: string | undefined,
  cronTimeZone: string | undefined,
  mode: ScheduleTimeDisplayMode,
  viewerTimeZone: string = getBrowserTimezone(),
  cronTimeFrame?: CronTimeFrame | null
): string {
  if (!cronTimeZone?.trim()) {
    return formatAdvancedCronFrequencyUtc(cronExpression, cronExplain);
  }

  if (mode === 'utc') {
    return (
      formatAdvancedCronFrequencyFromAuthorWindowInUtc(
        cronExpression,
        cronTimeZone,
        cronTimeFrame
      ) ?? formatAdvancedCronFrequencyUtc(cronExpression, cronExplain)
    );
  }

  const subType = classifyAdvancedCron(cronExpression);
  if (subType === 'recurring') {
    const parsed = parseRecurringCronFormState(cronExpression, cronTimeZone);
    if (parsed) {
      const form = hasStoredCronTimeFrame(cronTimeFrame)
        ? applyCronTimeFrameToForm(parsed, cronTimeFrame)
        : parsed;
      if (viewerTimeZone === cronTimeZone.trim()) {
        return describeLocalRecurringSchedule(recurringFormToScheduleConfig(form));
      }
      return formatAdvancedCronFrequencyInViewerLocal(form, cronTimeZone.trim(), viewerTimeZone);
    }
  }

  if (subType === 'recurring-fixed-time') {
    const parts = cronExpression.trim().split(/\s+/);
    const utcHour = parseInt(parts[1], 10);
    const utcMinute = parseInt(parts[0], 10);
    if (!Number.isNaN(utcHour) && !Number.isNaN(utcMinute)) {
      const local = convertUTCToLocalTime(utcHour, utcMinute, viewerTimeZone);
      return `At ${formatUtcWallClock(local.hour, local.minute)}`;
    }
  }

  if (subType === 'fixed-time' && cronExplain?.trim()) {
    return cronExplain
      .trim()
      .replace(/\s*\(UTC\)\s*$/i, '')
      .trim();
  }

  return formatAdvancedCronFrequencyUtc(cronExpression, cronExplain).replace(/\s*UTC\s*$/i, '');
}

/**
 * UTC-only advanced cron frequency (legacy null-TZ or UTC toggle).
 */
export function formatAdvancedCronFrequencyUtc(
  cronExpression: string,
  cronExplain: string | undefined
): string {
  if (!cronExpression?.trim() && !cronExplain?.trim()) {
    return '';
  }

  if (cronExpression?.trim()) {
    const subType = classifyAdvancedCron(cronExpression);

    if (subType === 'recurring') {
      const recurring = formatAdvancedRecurringFrequencyUtc(cronExpression);
      if (recurring) {
        return recurring;
      }
    }

    if (subType === 'fixed-time') {
      return formatAdvancedFixedTimeFrequencyUtc(cronExpression, cronExplain);
    }

    if (subType === 'recurring-fixed-time') {
      return formatAdvancedRecurringFixedFrequencyUtc(cronExpression, cronExplain);
    }
  }

  const raw =
    cronExplain?.trim() || (cronExpression ? describeCronExpressionUtc(cronExpression) : '');
  return appendUtcSuffix(raw);
}

/**
 * Parse stored UTC cron for edit form — converts UTC bytes to author-local wall clock.
 */
export function parseAdvancedCronForEdit(
  expression: string,
  timeZone: string,
  cronTimeFrame?: CronTimeFrame | null
): AdvancedCronEditState | null {
  if (!expression?.trim()) {
    return null;
  }

  const parts = expression.trim().split(/\s+/);
  if (parts.length < 5) {
    return null;
  }

  const [minuteField, hourField, domField, monthField, dowField, yearField] = parts;
  const year = yearField || '*';

  const recurring = parseRecurringCronFormState(expression, timeZone);
  if (recurring) {
    const form = hasStoredCronTimeFrame(cronTimeFrame)
      ? applyCronTimeFrameToForm(recurring, cronTimeFrame)
      : recurring;
    return recurringFormToEditState(form);
  }

  if (year !== '*' && /^\d{4}$/.test(year)) {
    const utcHour = parseInt(hourField, 10);
    const utcMinute = parseInt(minuteField, 10);
    const dom = parseInt(domField, 10);
    const mon = parseInt(monthField, 10);

    if (
      !Number.isNaN(utcHour) &&
      !Number.isNaN(utcMinute) &&
      !Number.isNaN(dom) &&
      !Number.isNaN(mon)
    ) {
      const local = convertUtcCronInstantToLocalWallClockSchedule(
        parseInt(year, 10),
        mon,
        dom,
        utcHour,
        utcMinute,
        timeZone
      );
      return {
        mode: 'fixed-time',
        fixedHour: local.hour,
        fixedMinute: local.minute,
        fixedYear: year,
        fixedMonth: local.month,
        fixedDay: local.day,
        dayOfMonth: String(local.day),
        month: String(local.month),
      };
    }
  }

  const utcHour = parseInt(hourField, 10);
  const utcMinute = parseInt(minuteField, 10);
  if (!Number.isNaN(utcHour) && !Number.isNaN(utcMinute)) {
    const local = convertUTCToLocalTime(utcHour, utcMinute, timeZone);
    return {
      mode: 'recurring-fixed-time',
      fixedHour: local.hour,
      fixedMinute: local.minute,
      dayOfWeek: dowField || '*',
      dayOfMonth: domField || '*',
      month: monthField || '*',
    };
  }

  return null;
}
